import { useEffect } from "react";
import { useTranslation } from "react-i18next";

/**
 * @cubone/react-file-manager bundles English-only UI strings. This hook patches
 * visible labels in the DOM when the active locale is Turkish.
 */
export function useFileManagerDomI18n(rootRef, active) {
  const { t, i18n } = useTranslation();
  const isTr = Boolean(i18n.language?.toLowerCase().startsWith("tr"));

  useEffect(() => {
    if (!active || !isTr) return;
    const root = rootRef.current;
    if (!root) return;

    const apply = () => {
      root.querySelectorAll(".empty-nav-pane").forEach((el) => {
        if (el.textContent.trim() === "Nothing here yet") {
          el.textContent = t("fileManager.emptyNav");
        }
      });

      root.querySelectorAll(".empty-folder").forEach((el) => {
        if (el.textContent.trim() === "This folder is empty.") {
          el.textContent = t("fileManager.emptyFolder");
        }
      });

      root.querySelectorAll(".breadcrumb .folder-name").forEach((el) => {
        if (el.textContent.trim() === "Home") {
          el.textContent = t("fileManager.home");
        }
      });

      const iconOnlyToolbar = root.querySelectorAll(
        ".fm-toolbar .toolbar-left-items > button.item-action.icon-only"
      );
      if (iconOnlyToolbar[0]) iconOnlyToolbar[0].title = t("fileManager.changeView");
      if (iconOnlyToolbar[1]) iconOnlyToolbar[1].title = t("fileManager.refresh");

      const primarySpans = root.querySelectorAll(
        ".fm-toolbar > div:first-child button.item-action:not(.icon-only) span"
      );
      if (primarySpans[0]) primarySpans[0].textContent = t("fileManager.newFolder");
      if (primarySpans[1]) primarySpans[1].textContent = t("fileManager.upload");

      root.querySelectorAll('.toggle-view li[role="menuitem"]').forEach((li) => {
        const spans = li.querySelectorAll("span");
        const nameSpan = spans[spans.length - 1];
        if (!nameSpan) return;
        const txt = nameSpan.textContent.trim();
        if (txt === "Grid") nameSpan.textContent = t("fileManager.grid");
        if (txt === "List") nameSpan.textContent = t("fileManager.list");
      });

      root.querySelectorAll(".fm-modal-heading").forEach((el) => {
        if (el.textContent.trim() === "Upload") {
          el.textContent = t("fileManager.uploadModalTitle");
        }
      });

      root.querySelectorAll(".draggable-file-input .input-text span").forEach((el) => {
        if (el.textContent.trim() === "Drag files to upload") {
          el.textContent = t("fileManager.dragFilesToUpload");
        }
      });

      root.querySelectorAll('label[for="chooseFile"]').forEach((el) => {
        if (el.textContent.trim() === "Choose File") {
          el.textContent = t("fileManager.chooseFile");
        }
      });
    };

    apply();

    let debounce;
    const obs = new MutationObserver(() => {
      clearTimeout(debounce);
      debounce = setTimeout(apply, 0);
    });
    obs.observe(root, { childList: true, subtree: true, characterData: true });
    return () => {
      clearTimeout(debounce);
      obs.disconnect();
    };
  }, [active, isTr, t, i18n.language]);
}
